// ── 휴대폰 월별 달력 (2026-09-29 출퇴근 관리 합치기 — 휴대폰도 달력이 기본, 사용자 확정) ─────
//   칸이 좁아(약 50px) 이름 칩 대신 **점(눈여겨볼 상태)과 출근 인원**만 그린다.
//   날짜를 누르면 아래에 그날 사람 목록. "자세히" 는 PC 와 같은 날짜 상세 창(수정·근무지·예외 등록).
//   사람을 누르면: 직원별 상세 탭으로(onPersonClick 이 있을 때) / 없으면 날짜 상세 창.
//   상태·"미출근" 판정은 PC 월별 달력(MonthView)과 같은 규칙.
import React, { useMemo } from 'react';
import { STATUS_META, WEEKDAY_NAMES, WEEKDAY_ORDER, getTodayYmd, buildMonthGrid } from './utils';

const WORKED = new Set(['present', 'missing_out', 'offday_work', 'holiday_work']);
const NOT_WORKDAY = new Set(['offday', 'holiday']);
// 점은 눈여겨볼 상태만, 이 순서로 최대 3개
const DOT_STATUSES = ['absent', 'missing_out', 'on_leave', 'half_leave'];
const LIST_ORDER = ['absent', 'missing_out', 'on_leave', 'half_leave', 'present', 'offday_work', 'holiday_work', 'scheduled', 'holiday', 'offday'];

// 오늘인데 예정 상태로 남은 근무일 = 미출근(결근 색) — MonthView 와 같은 규칙
function effectiveStatus(cell, ymd, todayYmd) {
  return (ymd === todayYmd && cell.status === 'scheduled' && cell.is_workday) ? 'absent' : cell.status;
}

function cellLabel(cell, status, ymd, todayYmd) {
  if (ymd === todayYmd && cell.status === 'scheduled' && cell.is_workday) return '미출근';
  if (status === 'half_leave' && cell.half_leave_type) return `반차(${cell.half_leave_type})`;
  return (STATUS_META[status] || STATUS_META.scheduled).label;
}

// pickedYmd/onPickYmd: 고른 날은 부모(AttendanceCalendar)가 들고 있다 — 저장 뒤 다시 불러오는 동안 이 화면이
//   사라졌다 다시 생겨도 고른 날이 오늘로 돌아가지 않게(검토에서 발견).
// 버튼에 print-keep: 달력 인쇄 CSS 가 버튼을 숨기는데, 날짜 칸·사람 줄은 인쇄에 나와야 한다.
export function MobileMonthView({ calData, year, month, filteredCells, onDayClick, onPersonClick, pickedYmd, onPickYmd }) {
  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const todayYmd = getTodayYmd();
  const dayMap = useMemo(() => {
    const map = {};
    (calData?.days || []).forEach((d) => { map[d.date] = d; });
    return map;
  }, [calData]);
  const monthPrefix = `${year}-${String(month).padStart(2, '0')}`;
  // 고른 날이 이 달이 아니면(달을 넘기면) 오늘 → 없으면 1일
  const selectedYmd = (pickedYmd && pickedYmd.startsWith(monthPrefix)) ? pickedYmd
    : (todayYmd.startsWith(monthPrefix) ? todayYmd : `${monthPrefix}-01`);

  const visible = (ymd) => (dayMap[ymd]?.cells || []).filter((c) => filteredCells.has(`${ymd}:${c.user_id}`));
  const selDay = dayMap[selectedYmd];
  const selCells = visible(selectedYmd)
    .map((c) => ({ c, st: effectiveStatus(c, selectedYmd, todayYmd) }))
    .filter(({ st }) => !(selectedYmd > todayYmd && st === 'scheduled'))
    .sort((a, b) => LIST_ORDER.indexOf(a.st) - LIST_ORDER.indexOf(b.st));
  const plannedCount = selectedYmd > todayYmd ? visible(selectedYmd).filter((c) => c.status === 'scheduled').length : 0;
  const count = (st) => selCells.filter((x) => x.st === st).length;
  // 요일 = 달력 칸의 열 위치(일~토). 날짜 계산에 new Date 를 쓰지 않는다(날짜 규칙).
  const selIdx = grid.indexOf(selectedYmd);
  const selWd = selDay?.weekday || (selIdx >= 0 ? WEEKDAY_ORDER[selIdx % 7] : null);
  const selTitle = `${month}월 ${Number(selectedYmd.slice(8))}일${selWd ? ` (${WEEKDAY_NAMES[selWd]})` : ''}`;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 }}>
        {WEEKDAY_ORDER.map((wd) => (
          <div key={wd} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: wd === 'sat' ? '#2563eb' : wd === 'sun' ? '#dc2626' : 'var(--text-sub)' }}>
            {WEEKDAY_NAMES[wd]}
          </div>
        ))}
        {grid.map((ymd, idx) => {
          if (!ymd) return <div key={`e-${idx}`} style={{ height: 52, borderRadius: 8, background: 'var(--bg-subtle)' }} />;
          const day = dayMap[ymd];
          const cells = visible(ymd).map((c) => effectiveStatus(c, ymd, todayYmd));
          const dots = DOT_STATUSES.filter((st) => cells.includes(st)).slice(0, 3);
          const workCells = cells.filter((st) => !NOT_WORKDAY.has(st));
          const showCount = ymd <= todayYmd && workCells.length > 0;
          const wd = day?.weekday;
          const selected = ymd === selectedYmd;
          return (
            <button
              key={ymd}
              type="button"
              className="print-keep"
              onClick={() => onPickYmd(ymd)}
              aria-label={`${Number(ymd.slice(8))}일`}
              aria-pressed={selected}
              style={{
                height: 52, boxSizing: 'border-box', borderRadius: 8, padding: '4px 2px', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
                background: day?.is_holiday ? 'rgba(220, 38, 38, 0.06)' : 'var(--bg-card)',
                border: selected ? '2px solid var(--text)' : (ymd === todayYmd ? '2px solid #3b82f6' : '1px solid var(--border)'),
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 800, color: day?.is_holiday || wd === 'sun' ? '#dc2626' : wd === 'sat' ? '#2563eb' : 'var(--text)' }}>
                {Number(ymd.slice(8))}
              </span>
              <span style={{ display: 'flex', gap: 2, height: 6 }}>
                {dots.map((st) => <span key={st} style={{ width: 6, height: 6, borderRadius: 3, background: STATUS_META[st].text }} />)}
              </span>
              <span style={{ fontSize: 9, color: 'var(--text-sub)' }}>
                {showCount ? `${workCells.filter((st) => WORKED.has(st)).length}/${workCells.length}` : ''}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, margin: '16px 0 8px' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{selTitle}{selDay?.is_holiday ? ` · ${selDay.holiday_name || '공휴일'}` : ''}</div>
          <div style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 2 }}>
            {plannedCount > 0
              ? `근무 예정 ${plannedCount}명`
              : `출근 ${selCells.filter((x) => WORKED.has(x.st)).length} · 결근 ${count('absent')} · 연차 ${count('on_leave')}`}
          </div>
        </div>
        {selDay && (
          <button type="button" onClick={() => onDayClick(selDay)}
            style={{ minHeight: 40, padding: '0 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            자세히
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {selCells.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
            {plannedCount > 0 ? '아직 기록이 없는 날입니다.' : '이 날짜에 표시할 기록이 없습니다.'}
          </div>
        )}
        {selCells.map(({ c, st }) => {
          const meta = STATUS_META[st] || STATUS_META.scheduled;
          const time = c.check_in ? `${c.check_in}${c.check_out ? `-${c.check_out}` : ''}` : '';
          return (
            <button
              key={c.user_id}
              type="button"
              className="print-keep"
              onClick={() => (onPersonClick ? onPersonClick(c.user_id, selectedYmd) : (selDay && onDayClick(selDay)))}
              style={{
                minHeight: 44, boxSizing: 'border-box', padding: '0 12px', borderRadius: 10, cursor: 'pointer',
                border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {time && <span style={{ fontSize: 12, color: 'var(--text-sub)' }}>{time}</span>}
                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: meta.bg, color: meta.text, border: `1px solid ${meta.border}` }}>
                  {cellLabel(c, st, selectedYmd, todayYmd)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
